<template>
    <div>
        <ui-crud title="Purchase Orders" subtitle="A list of purchase orders for this package"
                 ref="crud"
                 :collapsable="true"
                 :fetch="fetch" :pipe-with="pipeItems"
                 :group-by="groupByStatus">
            <template #new-button>
                <div class="flex items-center">
                    <h1 class="text-lg font-medium text-gray-900">Purchase Orders</h1>
                    <ui-button-new small class="ml-auto hidden sm:inline-flex" icon="fas fa-plus"
                                   @click.prevent="createNew()">New
                    </ui-button-new>
                </div>
                <p class="mt-1 text-sm text-gray-600">All Purchase Orders for this package</p>
                <ui-button-new class="sm:hidden mt-4" icon="fas fa-plus"
                               @click.prevent="createNew()">New
                </ui-button-new>
            </template>

            <!-- Filters. -->
            <template #filters>
                <div class="relative">
                    <ui-dropdown menu-class="p-3 w-72" placement="right">
                        <template #default="{ open,close }">
                            <ui-input class="mt-6" field="bg-gray-50 pr-10" name="search" @focus="open"
                                      @blur="$nextTick(() => close)"
                                      v-model="search"
                                      @input="debouncedUpdateItems(); open()"></ui-input>
                            <div
                                class="absolute right-0 inset-y-0 h-full flex items-center justify-center p-3 text-gray-400">
                                <i class="fas fa-search"></i>
                            </div>
                        </template>
                        <template #menu="{ close }">
                            <div class="p-1 max-h-64 overflow-y-auto w-full z-30">
                                <div v-if="supplierSearch.length > 0">
                                    <div class="ml-0 text-xs font-medium text-gray-400 mb-1">Existing Suppliers</div>
                                    <div class="ml-1 text-xs mb-1 mr-3">
                                        <div
                                            v-for="supplier in supplierSearch"
                                            class="flex items-center space-x-1 text-left w-full rounded-md px-2 py-1 focus:outline-none cursor-pointer text-gray-600 hover:bg-blue-50"
                                            @click="searchSupplier(supplier); close()"
                                            v-text="supplier"
                                        >
                                        </div>
                                    </div>
                                </div>
                                <div v-if="supplierSearch.length === 0">
                                    <div
                                        class="text-gray-400 text-xs font-italic p-3 justify-center align-middle items-center flex">
                                        No matching suppliers found
                                    </div>
                                </div>
                            </div>
                        </template>
                    </ui-dropdown>
                </div>
            </template>

            <!-- List item. -->
            <template #list-item="{ item, select, active, setEditMode }">
                <ui-crud-card
                    :ref="'item-'+item.id"
                    :title="(item.supplier?item.supplier:'<no supplier>')+' '+(item.fullReference)"
                    :subtitle="item.formatted_date"
                    :metadata="item.total"
                    :active="active"
                    @click="selectItem(item,select,setEditMode)"
                ></ui-crud-card>
            </template>

            <!-- Create form. -->
            <template #create="{ addItem, setEditMode, selectItem }">
                <div class="flex space-x-8 pt-4 px-4 m-4 justify-center items-center">
                    <div
                        class="border border-grey-200 rounded-l bg-gray-50 w-full h-full p-4 justify-center items-center text-center text-gray-500">
                        <-- Please select an existing Purchase Order to view/edit or click new to create a new one.
                    </div>
                </div>
            </template>

            <template #edit="{ selectedItem, setEditMode, removeItem }">
            </template>

            <!-- Show page. -->
            <template #show="{ selectedItem, setEditMode, removeItem, addItem, selectItem }">
                <purchase-order-form :item="selectedItem" :removeItem="removeItem" :search="search"
                                     :updateItems="updateItems" :fetch="fetch"
                                     ref="form"></purchase-order-form>
            </template>
        </ui-crud>
    </div>
</template>

<script>
import {PurchaseOrder} from "@models";
import UiInput from "../../ui/UiInput.vue";
import UiCrudCard from "../../ui/UiCrudCard.vue";
import UiCrud from "../../ui/UiCrud.vue";
import {uuid} from "../../../utils";
import moment from "moment";
import PurchaseOrderForm from "./PurchaseOrderForm.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import EstimateAssemblyPinger from "../../estimation/estimates/EstimateAssemblyPinger.vue";

export default {
    components: {
        EstimateAssemblyPinger,
        UiDropdownItem, UiDropdown, UiButtonNew, PurchaseOrderForm, UiCrud, UiCrudCard, UiInput
    },
    props: {
        packageReference: null,
        legacyPackageId: null,
        packageSiteAddress: null,
        packageName: null,
        admin: Boolean,
        showWhenLoaded: null
    },
    data() {
        return {
            search: '',
            items: [],
            debouncedUpdateItems: _.debounce(this.updateItems, 300),
            suppliers: [],
            resultsKey: 0,
        }
    },
    computed: {
        supplierSearch() {
            const query = this.search.toLowerCase().replace(/"/gm, ' ').replace('#supplier:', '').split(/[^A-Za-z0-9.-]+/);
            if (query.length) {
                return this.suppliers.filter((supplier) => {
                    return query.every((token) => {
                        return supplier.toLowerCase().includes(token);
                    });
                });
            }
            return this.suppliers;
        }
    },
    methods: {
        async updateItems() {
            this.$refs.crud.items = await this.fetch();
            this.items = this.$refs.crud.items
        },
        async createNew() {
            let id = uuid();
            this.search = '';
            await PurchaseOrder.create(this.packageReference, id, {
                date: moment().format("YYYY-MM-DD"),
                deliveryAddress: this.packageSiteAddress,
                packageName: this.packageName
            });
            await this.$refs.crud.fetchItems();
            this.$nextTick(() => {
                this.$refs.crud.selectItem(this.$refs.crud.items.find((i) => i.id === id));
                this.$nextTick(() => {
                    this.$refs.form.toggleEditMode(true)
                })
            })
        },
        searchSupplier(supplier) {
            this.search = '#supplier:"' + supplier + '"';
            this.updateItems();
        },
        selectItem(item, select, setEditMode) {
            select()
        },
        async fetch(itemId) {
            this.loaded = false
            this.$refs.crud.setLoadingIndex(true)
            this.items = await PurchaseOrder.search(this.packageReference, this.search)
            this.$refs.crud.setLoadingIndex(false)
            if (itemId) {
                this.$refs.crud.selectItem(this.items.find((i) => i.id === itemId))
            } else if (this.items.length) {
                this.$refs.crud.selectItem(this.items[0])
            }
            this.loaded = true
            this.searching = false;
            return this.items;
        },
        pipeItems(items) {
            const query = this.search.toLowerCase().split(/[^A-Za-z0-9.-]+/);
//            if (query.length) {
//                return items.filter((pr) => {
//                    return (this.$refs.crud.selectedItem && (pr.id == this.$refs.crud.selectedItem.id)) || query.every((token) => {
//                        return pr.tokens.includes(token);
//                    });
//                });
//            }
            return items;
        },
        groupByStatus(item) {
            return item.status + (item.voided ? ' (VOIDED)' : '')
        },
    },
    async mounted() {
        let urlParams = new URLSearchParams(window.location.search);
        let search = urlParams.get("search");
        if (search) {
            this.search = search
            urlParams.delete("search")
            if (history.replaceState) {
                let searchString = urlParams.toString().length > 0 ? '?' + urlParams.toString() : '';
                let newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + searchString + window.location.hash;
                history.replaceState(null, '', newUrl);
            }
            await this.updateItems();
        }
        this.suppliers = await PurchaseOrder.suppliers(this.packageReference)
        this.landing = false;
        if (this.showWhenLoaded) {
            this.$nextTick(() => {
                this.$refs.crud.fetch(this.showWhenLoaded)
            })
        }
    }
}
</script>